import CloudUpload from '@/assets/icons/CloudUpload';
import MapPin from '@/assets/icons/MapPin';
import { Button } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import { Switch } from '@/components/common/Switch';
import { Textarea } from '@/components/common/Textarea';
import { ACTIVITY_COLOR, ITEMS_STATUS, ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { ITEM_ACTIVITY, ITEM_STATUS, ITEM_TYPE, ROLE } from '@/types/enums';
import { toastify } from '@/utils/toastify';
import { cn, generateCouponCode } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { RefreshCcw, XIcon } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import MapForm from './MapForm';
import MapPreview from '@/components/common/MapPreview';
import ItemMeoFieldsSection from './ItemMeoFieldsSection';
const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png'];
const FILE_SIZE = 10 * 1024 * 1024;

interface Param {
    address: string,
    latitude: string,
    longitude: string
}
export default observer(function SettingFormItem() {
    // hooks
    const { t } = useTranslation();
    const nav = useNavigate();
    const { id } = useParams();

    // store
    const {
        itemStore: { addItem, getAllItem, uploadImage, getItemById, updateItem, item, paging, clean, keepSearchParams, defaultPaging },
        modalStore: { showAlertModal }
    } = useStore();

    const statusItem = ITEMS_STATUS.find(i => i.id === item?.status);
    const activityItem = ACTIVITY_COLOR.find(i => i.id === item?.activity);

    // state
    const [fileId, setFileId] = useState<string>('');
    const [imagePreview, setImagePreview] = useState<any>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [addressSelected, setAddressSelected] = useState<Param>({
        address: '',
        latitude: '',
        longitude: ''
    });

    // validate schema
    const validateSchema = yup.object().shape({
        title: yup.string().trim().required('form_error_validate.required'),
        description: yup.string().trim().required('form_error_validate.required'),

        cover: yup
            .mixed<string | File>()
            // F1 (2026-05-06): 商業ポストカード (COUPON) の表示画像は backend が
            // motif PNG を自動配信するため、画像アップロードは任意化。個人アイテム
            // (NOT_SET) は引き続き必須。
            .when('itemType', {
                is: value => ITEM_TYPE.COUPON === Number(value) || ITEM_TYPE.TOURIST_INFO === Number(value),
                then: schema => schema.notRequired(),
                otherwise: schema => schema
                    .required('form_error_validate.required')
                    .test('isEmpty', 'form_error_validate.required', (value) => {
                        return value !== undefined && value !== '';
                    })
            })
            .test('fileType', 'form_error_validate.format_image', (value) => {
                // F1: 商業ポストカード (COUPON / TOURIST_INFO) は画像任意なので
                // 未アップロード (= undefined / 空文字) は OK。
                if (value === undefined || value === '') {
                    return true;
                }
                if (typeof value === 'string') {
                    return true;
                }
                if (value instanceof File) {
                    return SUPPORTED_FORMATS.includes(value.type);
                }
                return false;
            })
            .test('fileSize', 'form_error_validate.maximum_size_image', (value) => {
                if (value === undefined || value === '') {
                    return true;
                }
                if (typeof value === 'string') {
                    return true;
                }
                return value instanceof File && value.size <= FILE_SIZE;
            }),

        itemType: yup.string().required('form_error_validate.required'),
        code: yup
            .string()
            .when('itemType', {
                is: value => ITEM_TYPE.COUPON === Number(value),
                then: _ => yup.string().notRequired(),
                otherwise: _ => yup
                    .string()
                    .notRequired()
            }),
        location: yup.string().trim().required('form_error_validate.required'),
        address: yup.string(),
        latitude: yup.string(),
        longitude: yup.string(),
        // イベント用マップ非表示フラグ (2026-07-22)
        hiddenOnMap: yup.boolean(),
        status: id ? yup.string() : yup.string().required('form_error_validate.required'),
        reason: yup.string(),
        // MEO landmark ads (Phase 1, B-1 schema fix, 2026-05-01):
        // ItemMeoFieldsSection の field を yup schema に登録しないと yupResolver が
        // unknown key として strip してしまい onSubmit data に届かない bug が発生。
        // 全て optional として登録する。値は ItemMeoFieldsSection 側で sentinel ('')
        // を使って未設定を表現するため、最小 validation のみ。
        fortune: yup.string(),
        motif: yup.string(),
        landmarkId: yup.string(),
        advertiserUserId: yup.string(),
        validFrom: yup.string(),
        validTo: yup.string(),
        maxCollections: yup.string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            title: '',
            description: '',
            cover: item?.file?.id ?? undefined,
            itemType: ITEM_TYPE.NOT_SET.toString(),
            code: '',
            location: addressSelected?.address ?? undefined,
            address: addressSelected?.address ?? '',
            latitude: addressSelected?.latitude ?? '',
            longitude: addressSelected?.longitude ?? '',
            status: ITEM_STATUS.PUBLISHED.toString(),
            reason: '',
            hiddenOnMap: false,
            // MEO landmark ads (Phase 1, B-1, 2026-05-01) — 商業ポストカード用 fields
            fortune: '',
            motif: '',
            landmarkId: '',
            advertiserUserId: '',
            validFrom: '',
            validTo: '',
            maxCollections: ''
        } as any
    });

    useEffect(() => {
        fetchData();
        return () => {
            // clean();
            form.clearErrors('cover');
            form.setValue('cover', '');
        };
    }, [id]);

    useEffect(() => {
        if (item?.userCreated.role_id === ROLE.END_USER) {
            nav(ROUTES.itemManagement.href);
        }
    }, [item]);

    useEffect(() => {
        form.setValue('location', addressSelected.address);
        form.setValue('address', addressSelected.address);
        form.setValue('latitude', addressSelected.latitude);
        form.setValue('longitude', addressSelected.longitude);
        form.clearErrors('location');
    }, [addressSelected]);

    useEffect(() => {
        return () => {
            if (imagePreview) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    const itemTypeWatch = form.watch('itemType');

    useEffect(() => {
        if (itemTypeWatch === ITEM_TYPE.COUPON.toString()) {
            form.trigger('code');
        } else {
            form.setValue('code', '');
            form.clearErrors('code');
        }
    }, [itemTypeWatch, id]);

    // functions
    const fetchData = async () => {
        if (id) {
            const res = await flowResult(getItemById(id));
            if (res?.ok) {
                form.setValue('title', res.data?.item, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                form.setValue('description', res.data?.description, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                form.setValue('cover', res.data.file?.path || undefined, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                form.setValue('itemType', res.data?.itemType.toString(), { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                form.setValue('code', res.data?.code, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                form.setValue('address', res.data?.address, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                form.setValue('latitude', res.data?.latitude, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                form.setValue('longitude', res.data?.longitude, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                form.setValue('status', res.data?.activity === ITEM_ACTIVITY.PRIVATE__EXPIRED ? ITEM_STATUS.PRIVATE.toString() : res?.data?.status, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                form.setValue('location', res.data?.address, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                // MEO landmark ads (Phase 1, B-1 prefill fix, 2026-05-01):
                // api ItemMapper.fileInfoMapper は camelCase で返すため、
                // 旧コード (snake_case 読み) は常に undefined → prefill 失敗していた。
                // 正: landmarkId / advertiserUserId / validTo (camelCase)
                // (Mapper 未対応 = validFrom / maxCollections / rewardType / rewardValue は
                // 別 PR で api 側 mapper 拡張要)
                if (res.data?.fortune != null) form.setValue('fortune' as any, String(res.data.fortune));
                if (res.data?.motif) form.setValue('motif' as any, res.data.motif);
                if (res.data?.landmarkId != null) form.setValue('landmarkId' as any, String(res.data.landmarkId));
                if (res.data?.advertiserUserId != null) form.setValue('advertiserUserId' as any, String(res.data.advertiserUserId));
                if (res.data?.validTo) form.setValue('validTo' as any, String(res.data.validTo).slice(0, 10));
                form.setValue('hiddenOnMap' as any, res.data?.hiddenOnMap === true);

                if (res.data?.activity === ITEM_ACTIVITY.PRIVATE__WITHDRAWAL) {
                    form.setValue('reason', res.data?.reason, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                }

                setFileId(res.data?.file?.id?.toString());
                setImagePreview(res.data?.file?.path);
            } else {
                nav(ROUTES.itemManagement.href);
            }
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const removeFile = () => {
        setImagePreview(null);
        setFileId('');
        form.setValue('cover', '', { shouldValidate: true });
        form.trigger('cover');
    };

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];

        if (file) {
            if (file.size > FILE_SIZE || !SUPPORTED_FORMATS.includes(file.type)) {
                setImagePreview(null);
                return;
            };
            const res = await flowResult(uploadImage({ file }));
            if (res?.ok) {
                const newImagePreview = URL.createObjectURL(file);
                setImagePreview(newImagePreview);
                setFileId(res.data.id);
                form.clearErrors('cover');
            } else {
                setImagePreview(null);
            }
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > FILE_SIZE || !SUPPORTED_FORMATS.includes(file.type)) {
                setImagePreview(null);
                return;
            };
            const res = await flowResult(uploadImage({ file }));
            if (res?.ok) {
                const newImagePreview = URL.createObjectURL(file);
                setImagePreview(newImagePreview);
                setFileId(res.data.id);
                form.setValue('cover', file);
                form.clearErrors('cover');
            } else {
                setImagePreview(null);
            }
        }
    };

    const handleClickLocation = () => {
        showAlertModal({
            size: '6xl',
            headerClassName: 'border-b border-b-border-7 pb-8',
            contentClassName: 'pb-0 pt-6',
            footerClassName: 'hidden',
            content: <MapForm setAddressSelected={setAddressSelected} />
        });
        form.setValue('location', addressSelected?.address ?? '');
    };

    const handleConfirmChange = () => {
        // form.getValues('title') || form.getValues('description') || form.getValues('address') || form.getValues('latitude') || form.getValues('longitude') || form.getValues('cover')
        showAlertModal({
            title: '確認',
            size: 'sm',
            headerClassName: 'border-b border-b-border-7',
            content: t('sentences.confirm_quit_form'),
            onSave: async () => {
                nav(ROUTES.itemManagement.href);
            }
        });
    };

    const onSubmit = async (data?: any) => {
        const { title, description, itemType, code, address, latitude, longitude, status,
            // MEO landmark ads (Phase 1, B-1, 2026-05-01) — 商業ポストカード用 fields (任意)
            fortune, motif, landmarkId, advertiserUserId, validFrom, validTo, maxCollections,
            hiddenOnMap } = data;
        const payload: any = {
            fileId,
            title,
            description,
            itemType: itemType === ITEM_TYPE.COUPON.toString() ? true : false,
            code: itemType === ITEM_TYPE.COUPON.toString() ? code : undefined,
            address,
            latitude,
            longitude,
            statusItem: Number(status),
            hiddenOnMap: hiddenOnMap === true
        };
        // MEO 拡張: 値が入っているもののみ送信 (api 側 undefined は無視)
        if (fortune !== '' && fortune != null) payload.fortune = Number(fortune);
        if (motif) payload.motif = motif;
        if (landmarkId !== '' && landmarkId != null) payload.landmarkId = Number(landmarkId);
        if (advertiserUserId !== '' && advertiserUserId != null) payload.advertiserUserId = Number(advertiserUserId);
        if (validFrom) payload.validFrom = validFrom;
        if (validTo) payload.validTo = validTo;
        if (maxCollections !== '' && maxCollections != null) payload.maxCollections = Number(maxCollections);
        if (id) {
            const res = await flowResult(updateItem({
                id,
                data: payload
            }));
            if (res?.ok) {
                toastify('success', t('messages.add_item_success'));
                getAllItem(keepSearchParams, paging);
                setFileId('');
                nav(ROUTES.itemManagement.href);
            } else {
                toastify('error', res?.message || 'このコードは既に使われています。他のコードを入力してください。');
            }
        } else {
            const res = await flowResult(addItem(payload));
            if (res && res?.ok) {
                clean();
                toastify('success', t('messages.add_item_success'));
                getAllItem(undefined, defaultPaging);
                setFileId('');
                nav(ROUTES.itemManagement.href);
            } else {
                const errorMessage = res?.message;
                if (errorMessage === 'クーポンコードは既に存在します。') {
                    form.setError('code', { message: 'クーポンコードは既に存在します。' });
                    return;
                }
                toastify('error', errorMessage);
            }
        }
    };

    return (
        <>
            <div className='layout-card'>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <div className='border border-background-6 !rounded-[5px] pb-4'>
                            <div className='px-6 py-3 text-[15px] font-semibold bg-background-6 rounded-t-sm'>
                                {t('words_title.item_info')}
                            </div>
                            <div className='mt-4 px-6'>
                                <div className='flex flex-wrap space-x-0 point-600:space-x-4'>
                                    <div className='flex-1'>
                                        <FormField
                                            control={form.control}
                                            name='title'
                                            render={({ field }) => (
                                                <>
                                                    <FormItem>
                                                        <Label className='mb-2' required>
                                                            {t('field.item_name')}
                                                        </Label>
                                                        <FormControl>
                                                            <Input
                                                                maxLength={255}
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                    </FormItem>
                                                    <FormMessage className='mt-1' />
                                                </>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name='description'
                                            render={({ field }) => (
                                                <>
                                                    <Label className='mt-4' required>
                                                        {t('field.descroption')}
                                                    </Label>
                                                    <FormItem className='w-full'>
                                                        <FormControl>
                                                            <Textarea
                                                                maxLength={1000}
                                                                className='resize-none h-[13.575rem] mt-2 whitespace-pre-line'
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                    </FormItem>
                                                    <FormMessage className='mt-1' />
                                                </>
                                            )}
                                        />
                                    </div>
                                    <div className='h-full w-full mt-6 point-600:mt-0 point-600:w-[343px]'>
                                        <FormField
                                            control={form.control}
                                            name='cover'
                                            render={({ field }) => (
                                                <div className='h-[341.5px] flex flex-col'>
                                                    <Label className='mb-2' required={itemTypeWatch === ITEM_TYPE.NOT_SET.toString()}>
                                                        {t('field.cover')}
                                                    </Label>
                                                    {/* F1 (2026-05-06): 商業ポストカードでは backend が motif PNG を自動配信するため、
                                                        ユーザの画像アップロードは admin 識別用にしか使われない。注意書きで明示。 */}
                                                    {(itemTypeWatch === ITEM_TYPE.COUPON.toString() || itemTypeWatch === ITEM_TYPE.TOURIST_INFO.toString()) && (
                                                        <p className='text-xs text-muted-foreground mb-2'>
                                                            ※ 商業ポストカード / 観光情報ではモチーフ画像が enduser 表示に使われるため、ここの画像アップロードは任意 (= admin 識別用のみ)。
                                                        </p>
                                                    )}
                                                    <FormItem className='w-full h-full flex-1'>
                                                        <FormControl>
                                                            <div
                                                                className={`h-full w-full border rounded-lg flex flex-col items-center justify-center bg-white relative ${form?.formState?.errors.cover ? 'border-[#ef4444] rounded-lg' : 'border-input bg-background-input rounded-lg'}`}
                                                                onDragOver={handleDragOver}
                                                                onDrop={(e) => {
                                                                    handleDrop(e);
                                                                    field.onChange(e.dataTransfer.files[0]);
                                                                }}
                                                                onDragLeave={handleDragLeave}
                                                            >
                                                                {imagePreview ?
                                                                    (
                                                                        <div className='text-center h-full w-full'>
                                                                            <button
                                                                                className='absolute top-2 right-2 bg-background-6 text-text-black-1 w-6 h-6 rounded-full flex items-center justify-center text-sm'
                                                                                onClick={removeFile}
                                                                            >
                                                                                <XIcon />
                                                                            </button>
                                                                            <label htmlFor='file-input' className='cursor-pointer w-full h-full flex'>
                                                                                <img
                                                                                    src={imagePreview}
                                                                                    alt='Preview'
                                                                                    className='w-full h-[312px] object-contain rounded-lg'
                                                                                />
                                                                            </label>
                                                                            <Input
                                                                                type='file'
                                                                                id='file-input'
                                                                                className='hidden'
                                                                                accept='image/*'
                                                                                onChange={(e) => {
                                                                                    handleFileChange(e);
                                                                                    field.onChange(e.target.files?.[0]);
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    ) :
                                                                    (
                                                                        <label htmlFor='file-input' className='h-full w-full cursor-pointer rounded-lg flex flex-col justify-center items-center bg-background-input'>
                                                                            <Input
                                                                                type='file'
                                                                                id='file-input'
                                                                                className='hidden h-full w-full'
                                                                                accept='image/*'
                                                                                onChange={(e) => {
                                                                                    handleFileChange(e);
                                                                                    field.onChange(e.target.files?.[0]);
                                                                                }}
                                                                            />
                                                                            <div
                                                                                className='cursor-pointer flex flex-col items-center text-gray-600 text-sm'
                                                                            >
                                                                                <CloudUpload />
                                                                                <div className='text-text-gray-1 text-[15px]'>{t('button.select_file')}</div>
                                                                                <div className='text-[15px] text-center text-text-gray-1 mt-2'>
                                                                                    <div>{t('sentences.drag_drop_file')}</div>
                                                                                    <div className='mt-[10px]'>
                                                                                        {t('sentences.maximum_size_image')}
                                                                                        <br />
                                                                                        {t('sentences.format_image')}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </label>
                                                                    )}
                                                            </div>
                                                        </FormControl>
                                                    </FormItem>
                                                    <FormMessage className='mt-1' />
                                                </div>
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className='mt-4'>
                                    <FormField
                                        control={form.control}
                                        name='itemType'
                                        render={({ field }) => (
                                            <FormItem>
                                                <Label required>
                                                    {t('field.item_type')}
                                                </Label>
                                                <FormControl>
                                                    <RadioGroup
                                                        // disabled={!!id}
                                                        key={field.value}
                                                        onValueChange={(value) => {
                                                            field.onChange(value);
                                                            form.trigger('code');
                                                        }}
                                                        value={field.value?.toString()}
                                                        className='mt-[10px]'
                                                    >
                                                        <div className='flex items-center space-x-2'>
                                                            <RadioGroupItem className='disabled:opacity-100 disabled:cursor-pointer' value={ITEM_TYPE.NOT_SET.toString()} id={ITEM_TYPE.NOT_SET.toString()} />
                                                            <Label className='text-text-gray-1' htmlFor={ITEM_TYPE.NOT_SET.toString()}>{t('field.not_set')}</Label>
                                                        </div>
                                                        <div className='flex items-center space-x-2'>
                                                            <RadioGroupItem className='disabled:opacity-100 disabled:cursor-pointer' value={ITEM_TYPE.COUPON.toString()} id={ITEM_TYPE.COUPON.toString()} />
                                                            <Label className='text-text-gray-1' htmlFor={ITEM_TYPE.COUPON.toString()}>{t('field.coupon')}</Label>
                                                        </div>
                                                        {/* F3 (2026-05-06): 観光情報 (TOURIST_INFO=2) ラジオを追加。
                                                            Phase 2 以降の予定枠だが、UI を先出しすることで現状の運用 (= COUPON のみ作成) と
                                                            将来運用の連続性を担保する。backend は item_type=2 を許容済 (CHECK constraint なし)。 */}
                                                        <div className='flex items-center space-x-2'>
                                                            <RadioGroupItem className='disabled:opacity-100 disabled:cursor-pointer' value={ITEM_TYPE.TOURIST_INFO.toString()} id={ITEM_TYPE.TOURIST_INFO.toString()} />
                                                            <Label className='text-text-gray-1' htmlFor={ITEM_TYPE.TOURIST_INFO.toString()}>観光情報 (Phase 2 以降の予定枠)</Label>
                                                        </div>
                                                    </RadioGroup>
                                                </FormControl>
                                                <FormMessage className='mt-1' />
                                            </FormItem>
                                        )}
                                    />
                                    {
                                        itemTypeWatch === ITEM_TYPE.COUPON.toString() && (
                                            <FormField
                                                control={form.control}
                                                name='code'
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <div className='flex items-center mt-[10px] gap-[10px]'>
                                                                <Input
                                                                    className='w-[236px] disabled:cursor-default !text-text-normal !opacity-100'
                                                                    placeholder={t('field.coupon_code')}
                                                                    {...field}
                                                                />
                                                                {/* <button onClick={() => form.setValue('code', generateCouponCode())} type='button'>
                                                                    <RefreshCcw className='size-6' color='#ffad00' />
                                                                </button> */}
                                                            </div>
                                                        </FormControl>
                                                        <FormMessage className='mt-1' />
                                                    </FormItem>
                                                )}
                                            />
                                        )
                                    }

                                </div>
                                <div className='mt-4'>
                                    <FormField
                                        control={form.control}
                                        name='location'
                                        render={({ field }) => (
                                            <FormItem>
                                                <Label required>
                                                    {t('field.location')}
                                                </Label>
                                                <FormControl>
                                                    <Button
                                                        onClick={() => handleClickLocation()}
                                                        variant='ghost'
                                                        type='button'
                                                        className='bg-none p-0 h-6 mt-2 hover:bg-white flex items-center space-x-1'
                                                    >
                                                        <MapPin />
                                                        <div className='text-[15px] text-[#3B7CEC]'>{t('button.add_location')}</div>
                                                    </Button>
                                                </FormControl>
                                                <FormMessage className='mt-1' />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <div className='mt-[10px]'>
                                    <FormField
                                        control={form.control}
                                        name='address'
                                        render={({ field }) => (
                                            <FormItem>
                                                <Label>
                                                    {t('field.address')}
                                                </Label>
                                                <FormControl>
                                                    <Input
                                                        disabled
                                                        {...field}
                                                        className='mt-2 disabled:text-text-normal disabled:opacity-100 disabled:cursor-default'
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <div className='mt-[10px] grid grid-cols-2 gap-4'>
                                    <FormField
                                        control={form.control}
                                        name='latitude'
                                        render={({ field }) => (
                                            <FormItem>
                                                <Label>
                                                    {t('field.latitude')}
                                                </Label>
                                                <FormControl>
                                                    <Input
                                                        disabled
                                                        {...field}
                                                        className='mt-2 disabled:text-text-normal disabled:opacity-100 disabled:cursor-default'
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name='longitude'
                                        render={({ field }) => (
                                            <FormItem>
                                                <Label>
                                                    {t('field.longitude')}
                                                </Label>
                                                <FormControl>
                                                    <Input
                                                        disabled
                                                        {...field}
                                                        className='mt-2 disabled:text-text-normal disabled:opacity-100 disabled:cursor-default'
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                {/* MEO landmark ads (Phase 1, Mapbox UX 改修 案 A, 2026-05-01):
                                    inline preview。lat/lng が両方有効値の時だけ表示。
                                    ピンドラッグで座標を fine-tune し、form の lat/lng を即時更新する。 */}
                                {(() => {
                                    const lat = Number(form.watch('latitude'));
                                    const lng = Number(form.watch('longitude'));
                                    if (!isFinite(lat) || !isFinite(lng) || (lat === 0 && lng === 0)) {
                                        return null;
                                    }
                                    return (
                                        <div className='mt-4'>
                                            <Label>{t('field.location_preview') || '配置プレビュー (ピンをドラッグして微調整可)'}</Label>
                                            <div className='mt-2'>
                                                <MapPreview
                                                    latitude={lat}
                                                    longitude={lng}
                                                    draggable
                                                    onMove={(nLat, nLng) => {
                                                        form.setValue('latitude', String(nLat));
                                                        form.setValue('longitude', String(nLng));
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })()}
                                {/* イベント用マップ非表示フラグ (2026-07-22 spec §2-3) */}
                                <div className='mt-4'>
                                    <FormField
                                        control={form.control}
                                        name={'hiddenOnMap' as any}
                                        render={({ field }) => (
                                            <FormItem>
                                                <div className='flex items-center space-x-3'>
                                                    <FormControl>
                                                        <Switch
                                                            checked={field.value === true}
                                                            onCheckedChange={checked => field.onChange(checked)}
                                                        />
                                                    </FormControl>
                                                    <Label>{t('field.hidden_on_map')}</Label>
                                                </div>
                                                <p className='text-[13px] text-text-gray-1 mt-1'>{t('field.hidden_on_map_hint')}</p>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                {/* MEO 商業ポストカード設定 (任意): クーポン item_type の時に意味を持つ */}
                                <ItemMeoFieldsSection form={form} />
                                {
                                    id && item?.activity === ITEM_ACTIVITY.PRIVATE__EXPIRED && (
                                        <div className='mt-4'>
                                            <FormField
                                                control={form.control}
                                                name='status'
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <Label required>
                                                            {t('field.status')}
                                                        </Label>
                                                        <FormControl>
                                                            <RadioGroup
                                                                key={field.value}
                                                                onValueChange={(value) => {
                                                                    field.onChange(value);
                                                                }}
                                                                value={field.value?.toString()}
                                                                className='mt-[10px]'
                                                            >
                                                                <div className='flex items-center space-x-2'>
                                                                    <RadioGroupItem className='' value={ITEM_STATUS.PRIVATE.toString()} id='private' />
                                                                    <Label className='text-text-gray-1' htmlFor='private'>{t('field.private')}</Label>
                                                                </div>
                                                                <div className='flex items-center space-x-2'>
                                                                    <RadioGroupItem value={ITEM_STATUS.PUBLISHED.toString()} id='publish' />
                                                                    <Label className='text-text-gray-1' htmlFor='publish'>{t('field.publish')}</Label>
                                                                </div>
                                                            </RadioGroup>
                                                        </FormControl>
                                                        <FormMessage className='mt-1' />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                    )
                                }
                                {
                                    id && item?.status === ITEM_STATUS.UN_PUBLISHED && (
                                        <div className='mt-4'>
                                            <FormField
                                                control={form.control}
                                                name='status'
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <Label required>
                                                            {t('field.status')}
                                                        </Label>
                                                        <FormControl>
                                                            <RadioGroup
                                                                key={field.value}
                                                                onValueChange={(value) => {
                                                                    field.onChange(value);
                                                                }}
                                                                value={field.value?.toString()}
                                                                className='mt-[10px]'
                                                            >
                                                                <div className='flex items-center space-x-2'>
                                                                    <RadioGroupItem className='' value={ITEM_STATUS.UN_PUBLISHED.toString()} id='unpublish' />
                                                                    <Label className='text-text-gray-1' htmlFor='unpublish'>{t('field.unpublish')}</Label>
                                                                </div>
                                                                <div className='flex items-center space-x-2'>
                                                                    <RadioGroupItem value={ITEM_STATUS.PUBLISHED.toString()} id='publish' />
                                                                    <Label className='text-text-gray-1' htmlFor='publish'>{t('field.publish')}</Label>
                                                                </div>
                                                            </RadioGroup>
                                                        </FormControl>
                                                        <FormMessage className='mt-1' />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                    )
                                }
                                {
                                    id && !(item?.activity === ITEM_ACTIVITY.PRIVATE__EXPIRED || (item?.activity === ITEM_ACTIVITY.NONE && item?.status === ITEM_STATUS.UN_PUBLISHED)) && (
                                        <div className='mt-4'>
                                            <div className='flex mt-[10px] items-center space-x-6'>
                                                <Label className='w-[103px]'>{t('field.status')}</Label>
                                                <div className='flex-1 text-active font-semibold'>
                                                    {statusItem && (
                                                        <div className={`${statusItem.textColor}`}>
                                                            {t(statusItem.name)}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }
                                {
                                    id && (item?.status !== ITEM_STATUS.UN_PUBLISHED) && (
                                        <div className='mt-4'>
                                            <div className='flex mt-[10px] items-center space-x-6'>
                                                <Label>{t('field.activity_status')}</Label>
                                                <div className='flex-1 text-active font-semibold'>
                                                    {activityItem && (
                                                        <Button type='button' variant='default' className={`${activityItem?.style} cursor-default w-[145px] h-[31px] text-white !rounded-[30px] text-[13px]`}>
                                                            {' '}
                                                            {t(activityItem?.name)}
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }
                                {
                                    !id && (
                                        <div className='mt-4'>
                                            <FormField
                                                control={form.control}
                                                name='status'
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <Label required>
                                                            {t('field.status')}
                                                        </Label>
                                                        <FormControl>
                                                            <RadioGroup
                                                                key={field.value}
                                                                onValueChange={(value) => {
                                                                    field.onChange(value);
                                                                }}
                                                                value={field?.value?.toString()}
                                                                className='mt-[10px]'
                                                            >
                                                                <div className='flex items-center space-x-2'>
                                                                    <RadioGroupItem value={ITEM_STATUS.PUBLISHED.toString()} id='yes' />
                                                                    <Label className='text-text-gray-1' htmlFor='yes'>{t('field.publish')}</Label>
                                                                </div>
                                                                <div className='flex items-center space-x-2'>
                                                                    <RadioGroupItem className='' value={ITEM_STATUS.UN_PUBLISHED.toString()} id='not' />
                                                                    <Label className='text-text-gray-1' htmlFor='not'>{t('field.unpublish')}</Label>
                                                                </div>
                                                            </RadioGroup>
                                                        </FormControl>
                                                        <FormMessage className='mt-1' />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                    )
                                }
                                {
                                    id && (item?.activity === ITEM_ACTIVITY.PRIVATE__WITHDRAWAL) && (
                                        <div className='mt-4'>
                                            <FormField
                                                control={form.control}
                                                name='reason'
                                                render={({ field }) => (
                                                    <>
                                                        <Label>{t('field.reason')}</Label>
                                                        <FormItem className='w-full'>
                                                            <FormControl>
                                                                <Textarea
                                                                    disabled
                                                                    className='resize-none disabled:text-text-normal disabled:opacity-100 disabled:cursor-default h-[6.875rem] mt-2 whitespace-pre-line'
                                                                    {...field}
                                                                />
                                                            </FormControl>
                                                        </FormItem>
                                                    </>
                                                )}
                                            />
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                        <div className='mt-6 flex justify-center items-center space-x-[10px]'>
                            <Button
                                type='button'
                                variant='outline'
                                className={cn('w-[166.5px] text-[15px] font-semibold h-12 hover:text-text-black-1 border border-border-button text-text-black-1 !rounded-[10px]')}
                                onClick={() => handleConfirmChange()}
                            >
                                {t('button.cancel')}
                            </Button>
                            <Button
                                type='submit'
                                variant='active'
                                className={cn('w-[166.5px] text-[15px] font-semibold h-12 bg-active hover:opacity-80 hover:text-white transition-opacity duration-300 !rounded-[10px]')}
                            >
                                {t('button.save')}
                            </Button>
                        </div>
                    </form>
                </Form>
            </div>
        </>
    );
});
